<template>
  <!-- Show message if no usages defined in General -->
  <section v-if="!sharedUsages || sharedUsages.length === 0" class="my-24 core-alert-zone">
    {{ $t('project.setup.sections.definition.no_usages_defined') }}
  </section>

  <!-- Show corrections for shared usages -->
  <section v-else class="mt-8">
    <!-- Manual area corrections -->
    <ManualAreasInput
      v-if="modelValue && sharedUsages.length"
      v-model="usageCorrections"
      :sharedUsages="sharedUsages"
      :quantityArea="quantityArea"
      :role="role" />
  </section>

  <!-- Hidden cost fields (kept for backward compatibility) -->
  <section class="hidden mt-8" v-if="modelValue">
    <h2 class="text-lg">{{ wt('definition', 'cost', 'title') }}</h2>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6 xl:grid-cols-3">
      <div>
        <label class="core-form-label">{{ wt('definition', 'cost', 'constructionCost') }}</label>
        <div class="flex items-center">
          <NumberInput
            v-model="modelValue.constructionCost"
            :formatBigNumber="true"
            :class="{
              'core-form-error': validator.getErrors('constructionCost'),
            }"
            class="mb-0" />
          <div class="w-24 ml-3 text-neutral-550">{{ $t('general.currency') }}</div>
        </div>
        <ErrorMessage name="constructionCost" class="error-message" />
      </div>
      <div>
        <label class="core-form-label">{{ wt('definition', 'cost', 'buildingCost') }}</label>
        <div class="flex items-center">
          <NumberInput
            v-model="modelValue.buildingCost"
            :formatBigNumber="true"
            :class="{
              'core-form-error': validator.getErrors('buildingCost'),
            }"
            class="mb-0" />
          <div class="w-24 ml-3 text-neutral-550">{{ $t('general.currency') }}</div>
        </div>
        <ErrorMessage name="buildingCost" class="error-message" />
      </div>
    </div>
  </section>
</template>

<script
  lang="ts"
  setup
  generic="T extends { buildingCost: number; constructionCost: number; usageCorrections: UsageCorrection[] }">
import NumberInput from '@/components/partials/inputs/NumberInput.vue'
import ManualAreasInput from '@/components/partials/inputs/ManualAreasInput.vue'
import ErrorMessage from '@/components/shared/ErrorMessage.vue'
import { useValidator } from '@/validation/form-validator'
import { computed, toRef, type PropType } from 'vue'
import type { SharedUsageItem, UsageCorrection } from '@/schemas/general/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

export interface SetupCalculator {
  setDefaultUsageItem: () => void
}

const props = defineProps<{
  role: Role
  setupCalculator: SetupCalculator
  sharedUsages: SharedUsageItem[]
  quantityArea: number
}>()

const modelValue = defineModel({ type: Object as PropType<T> })
const { wt } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))
const validator = useValidator()

const usageCorrections = computed<UsageCorrection[]>({
  get: () => modelValue.value?.usageCorrections ?? [],
  set: (value) => {
    if (!modelValue.value) return
    modelValue.value = {
      ...modelValue.value,
      usageCorrections: value,
    }
  },
})
</script>
